import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useFetcher } from 'react-router';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import type { z } from 'zod';

import { useTracker } from '@archipro-website/tracker';
import { useLocalisation } from '@archipro-website/localisation';
import type { SubmitActionResponse } from '~/routes/remix-api.enquiry.submit';
import { useAuth } from '@modules/root/context';
import {
    assertErrorResponse,
    assertSuccessResponse,
} from '@modules/root/graphql/responses';
import { useUserLoggedIn } from '@modules/user';
import { usePublicUser } from '@modules/user/hook/use-public-user';
import { trackSubmitCreateEnquiryRequest } from '@modules/tracking/util/trackSubmitCreateEnquiryRequest';
import { trackBrochureRequest } from '@modules/tracking/util/trackBrochureRequest';
import { trackProjectEnquire } from '@modules/tracking/util/trackProjectEnquiry';
import type { ProfessionalInitialResult } from '@modules/professional/hook/use-professional-initial-route-data';
import type { CreateEnquirySource } from '@modules/enquiry';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import {
    ENQUIRY_AUTO_SUBMIT_STORAGE_KEY,
    ENQUIRY_SESSION_STORAGE_KEY,
} from '../constants';
import {
    getEnquiryFormSchema,
    type enquiryFormSessionSchema,
} from '../validation';
import { getDefaultEnquiryMessage } from '../util/get-default-enquiry-message';
import { isEnquiryDraftClosed } from '../util/clear-enquiry-draft';
import { trackCreatedEnquiry } from '~/modules/tracking/util/trackCreatedEnquiry';
import { trackEnquiryAuthPrompt } from '~/modules/tracking/util/trackEnquiryFunnelStage';
import { useBulkEnquiryFanOut } from '../bulk/bulk-enquiry-state';
import { trackAuthEvent } from '~/modules/user/util/track-auth-event';
import { useAtomEvent } from '~/utils-rocco/hooks/use-atom-event';
import type { BuildHubFieldSelection } from './use-build-hub-field-selection';

const ENQUIRY_FETCHER_KEY = 'enquiry';

/**
 * Strips non-string values from a postcode value record, replacing them with `undefined`.
 * This ensures the postcode matches the form schema which only accepts string or undefined values.
 */
function stripNonStringValues(
    value: Record<string, unknown>,
): Record<string, string | undefined> {
    const stripped: Record<string, string | undefined> = {};
    for (const [k, v] of Object.entries(value)) {
        stripped[k] = typeof v === 'string' ? v : undefined;
    }
    return stripped;
}

/** Strips `null` values from a session-stored postcode so it matches the form schema which only accepts `undefined`. */
function stripPostcodeNulls(
    postcode:
        | { header: string; value: Record<string, unknown> }
        | null
        | undefined,
): { header: string; value: Record<string, string | undefined> } | undefined {
    // Treat a missing value as no postcode — a stored postcode without a value
    // record would otherwise crash stripNonStringValues' Object.entries.
    if (!postcode || !postcode.value) return undefined;
    return {
        header: postcode.header,
        value: stripNonStringValues(postcode.value),
    };
}

/**
 * Master switch for the one-tap Quick Enquiry feature.
 * Set to `true` to enable the QuickEnquiryCard for logged-in users
 * who have all required profile data.
 */
const QUICK_ENQUIRY_ENABLED = false;

/**
 * Attempts to parse the JSON-encoded Suburb field from the user's profile
 * into the postcode shape used by the enquiry form.
 * Returns undefined if the value is missing or malformed.
 */
function parseSuburbFromProfile(
    suburb: string | null | undefined,
): { header: string; value: Record<string, string | undefined> } | undefined {
    if (!suburb) return undefined;
    try {
        const parsed = JSON.parse(suburb);
        if (parsed?.header && parsed?.value) {
            return {
                header: parsed.header,
                value: stripNonStringValues(parsed.value),
            };
        }
    } catch {
        // Malformed JSON – ignore
    }
    return undefined;
}

/**
 * The saved enquiry form session.
 */
export const enquirySessionAtom = atomWithStorage<Partial<
    z.infer<typeof enquiryFormSessionSchema>
> | null>(
    ENQUIRY_SESSION_STORAGE_KEY,
    null,
    createJSONStorage(() => sessionStorage),
);
/**
 * The ID of the enquiry form. Each `useEnquiry` hook is assigned a unique
 * ID to make sure the post-submission `useEffect` logic is run only for that
 * specific submission. A page may have multiple enquiry forms (e.g. in-page and modal).
 */
const enquiryFormIdAtom = atom<string | null>(null);
/**
 * Controls whether the enquiry form should auto submit after the user logs in.
 */
export const enquiryAutoSubmitAtom = atomWithStorage<boolean>(
    ENQUIRY_AUTO_SUBMIT_STORAGE_KEY,
    false,
    createJSONStorage(() => sessionStorage),
);
/**
 * The last enquiry form submission was auto-submitted after login.
 */
const isAutoSubmittedAtom = atom(false);
/**
 * We have to wait for the page to refresh after login before we auto submit
 * the enquiry. The refresh after login happens in
 * {@link packages/web/app/modules/root/util/handle-auth-redirect.ts}.
 * This is a workaround to make sure the success animation is visible.
 * TODO: Remove when we don't refresh the page after login.
 */
const autoSubmitDelayAtom = atom(false);

const enquirySuccessAtom = atom<number>(0);

/**
 * A post-auth resubmit that came back an error. Distinct from the auth wall the
 * guest submit hits on the way in — that one is expected and is what opens the
 * gate — so only the resubmit's own outcome fires this.
 */
const enquiryFailureAtom = atom<number>(0);

export type EnquiryProfessional = Pick<
    ProfessionalInitialResult,
    'ID' | 'Title' | 'branchOptions'
> &
    Partial<
        Pick<
            ProfessionalInitialResult,
            | 'IsManufacturer'
            | 'HasProducts'
            | 'ProductsAllowed'
            | 'EnquiryMessageOverrideEnabled'
            | 'EnquiryMessagePlaceholder'
        >
    >;

export interface UseEnquiryProps {
    /** The professional to submit an enquiry to. */
    professional: EnquiryProfessional;
    /** The type of the Library Item (professional, product, project, article). */
    itemType: 'professional' | 'product' | 'project' | 'article';
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: number;
    /**
     * Bulk enquiry: the SiteTree ids of every selected item. When set, these are
     * submitted as `EnquiryInput.SiteTreeIDs` in place of the single `siteTreeID`
     * (which is still used for session keying and tracking), and the enquiry is
     * sent with no explicit targets so the backend fans it out to each item's
     * owning professional — giving every supplier only their own items.
     */
    siteTreeIDs?: number[];
    /** The source of the enquiry. */
    source: CreateEnquirySource;
    /** The project details if the enquiry is for a project. Used for tracking. */
    relatedItem?: {
        id: number;
        type: 'project';
        title: string;
        category: string;
    };
    /** Include the branch dropdown if the professional has branches. Defaults to `false`. */
    includeBranch?: boolean;
    /**
     * Collect a suburb / postcode and send it with the enquiry. Defaults to
     * `false`. Only the legacy inline form still asks: the revamped forms let
     * the attached BuildHub project carry the location, which is the project's
     * rather than the reader's home address.
     */
    collectLocation?: boolean;
    /** The enquiry form is in a modal. Defaults to `false`. */
    isModal?: boolean;
    /** The name of the item being enquired about (e.g. product name). Used to pre-fill the message. */
    itemTitle?: string;
    /** The CTA label that opened the form. Used to tailor the pre-filled message. */
    ctaLabel?: string;
    /** Called when the enquiry is successfully submitted. */
    onSuccess?: () => void;
    /** Called when the enquiry fails to submit. */
    onFailure?: () => void;
    /** Called when the user successfully logs in. */
    onAuthSuccess?: () => void;
    /** Called when the user cancels login. */
    onAuthCancel?: () => void;
    /**
     * Intercepts the guest auth wall. When provided, a guest submit that comes
     * back auth-required calls this with the recognised public user instead of
     * opening the site-wide auth modal, so the caller (the revamped enquiry
     * gate) can host the auth step itself. The auto-submit priming and all
     * tracking still fire identically; only the modal hand-off differs.
     */
    onAuthRequired?: (publicUser: PublicUser) => void;
}

/**
 * Hook for the enquiry form. Contains the form schema, default form values,
 * and functions to submit the enquiry and save the enquiry session.
 */
export function useEnquiry({
    professional,
    itemType,
    siteTreeID,
    siteTreeIDs,
    source,
    relatedItem,
    includeBranch = false,
    collectLocation = false,
    isModal = false,
    itemTitle,
    ctaLabel,
    onSuccess,
    onFailure,
    onAuthSuccess,
    onAuthCancel,
    onAuthRequired,
}: UseEnquiryProps) {
    const id = useId();
    const { isLoggedIn, user } = useUserLoggedIn();
    const { showAuthModal, hideAuthModal } = useAuth();
    const { setPublicUser } = usePublicUser('enquiry');
    const tracker = useTracker();
    const fanOut = useBulkEnquiryFanOut();
    /**
     * The fan-out of the submission that is in flight. Success can arrive after
     * the selection has been emptied, so the figures reported are the ones
     * actually sent rather than whatever is left by then.
     */
    const sentFanOutRef = useRef<typeof fanOut | undefined>(undefined);
    const fetcher = useFetcher<SubmitActionResponse>({
        key: ENQUIRY_FETCHER_KEY,
    });
    const suburbFetcher = useFetcher({ key: 'update-suburb' });
    const [enquirySession, setEnquirySession] = useAtom(enquirySessionAtom);
    const [enquiryFormId, setEnquiryFormId] = useAtom(enquiryFormIdAtom);
    const [enquiryAutoSubmit, setEnquiryAutoSubmit] = useAtom(
        enquiryAutoSubmitAtom,
    );
    const [isAutoSubmitted, setIsAutoSubmitted] = useAtom(isAutoSubmittedAtom);
    const [autoSubmitDelay, setAutoSubmitDelay] = useAtom(autoSubmitDelayAtom);
    const { triggerSuccess } = useEnquiryEvent({ onSuccess });
    const { country } = useLocalisation();
    const formSchema = useMemo(
        () =>
            getEnquiryFormSchema({
                fullName: !isLoggedIn || !!user.LastName,
                includeBranch:
                    includeBranch && !!professional.branchOptions?.length,
                country,
                collectLocation,
            }),
        [
            isLoggedIn,
            user,
            professional,
            includeBranch,
            country,
            collectLocation,
        ],
    );
    const defaultBranchId = useMemo(() => {
        if (!includeBranch || !professional.branchOptions.length) {
            return undefined;
        }
        if (
            enquirySession?.branchId &&
            professional.branchOptions.some(
                (branch) => branch.value.branchId === enquirySession.branchId,
            )
        ) {
            return enquirySession.branchId.toString();
        }
        const self = professional.branchOptions.find(
            (branch) => branch.value.branchId === professional.ID,
        );
        if (self) return self.value.branchId.toString();
        // A single branch is the only legal answer, so it is the default
        // rather than something a later effect has to fill in. The gate hides
        // the picker in that case, and a value written after paint is cleared
        // again by the re-hydration reset — leaving the send blocked on a
        // required field that is not on screen.
        return professional.branchOptions.length === 1
            ? professional.branchOptions[0]?.value.branchId.toString()
            : undefined;
    }, [enquirySession, includeBranch, professional]);
    // A professional can override the auto-populated enquiry copy via their
    // Business Manager settings. When the override is enabled the message box
    // starts empty; their placeholder (if any) becomes the textarea hint,
    // otherwise the form's generic hint shows. When disabled, the generated
    // default message pre-fills as normal.
    const overrideEnabled = professional.EnquiryMessageOverrideEnabled === true;
    const messagePlaceholder = overrideEnabled
        ? professional.EnquiryMessagePlaceholder?.trim() || undefined
        : undefined;
    // The generic auto-populated message. Still computed when the override is
    // enabled so we can recognise (and discard) a stale session copy of it below.
    const genericDefaultMessage = useMemo(
        () =>
            getDefaultEnquiryMessage({
                itemType,
                itemTitle,
                ctaLabel,
                professional,
            }),
        [itemType, itemTitle, ctaLabel, professional],
    );
    const defaultMessage = overrideEnabled ? '' : genericDefaultMessage;
    const profilePostcode = useMemo(
        () => (isLoggedIn ? parseSuburbFromProfile(user.Suburb) : undefined),
        [isLoggedIn, user],
    );

    const defaultFormValues = useMemo(() => {
        const isCurrentSession = enquirySession?.siteTreeId === siteTreeID;
        const sessionMessage = isCurrentSession
            ? enquirySession?.message
            : undefined;
        // With the override on the box should start empty. Ignore a stored
        // session message that is just the previously auto-filled generic copy
        // (unedited), but keep a genuine user draft (e.g. preserved across login).
        const useSessionMessage =
            !!sessionMessage &&
            !(overrideEnabled && sessionMessage === genericDefaultMessage);
        const message = (useSessionMessage && sessionMessage) || defaultMessage;
        const postcode =
            stripPostcodeNulls(enquirySession?.postcode) ?? profilePostcode;
        return isLoggedIn
            ? {
                  name: `${user.FirstName} ${user.LastName}`.trim(),
                  email: user.Email,
                  phoneNumber:
                      user.Phone || (enquirySession?.phoneNumber ?? ''),
                  postcode,
                  branchId: defaultBranchId,
                  buildHubId: enquirySession?.buildHubId
                      ? String(enquirySession.buildHubId)
                      : undefined,
                  message,
                  files: enquirySession?.files ?? [],
              }
            : {
                  name: enquirySession?.name ?? '',
                  email: enquirySession?.email ?? '',
                  phoneNumber: enquirySession?.phoneNumber ?? '',
                  postcode: stripPostcodeNulls(enquirySession?.postcode),
                  branchId: defaultBranchId,
                  buildHubId: enquirySession?.buildHubId
                      ? String(enquirySession.buildHubId)
                      : undefined,
                  message,
                  files: enquirySession?.files ?? [],
              };
    }, [
        isLoggedIn,
        user,
        enquirySession,
        defaultBranchId,
        defaultMessage,
        genericDefaultMessage,
        overrideEnabled,
        profilePostcode,
        siteTreeID,
    ]);

    /**
     * Merge a few fields into the saved session, leaving the rest alone.
     *
     * `saveEnquirySession` rebuilds the whole session from a form snapshot,
     * which is right when the reader is editing and wrong when a background
     * effect owns one field: it writes the form's identity values back over a
     * session those fields were deliberately cleared from.
     */
    const patchEnquirySession = useCallback(
        (patch: Partial<z.infer<typeof enquiryFormSessionSchema>>) => {
            if (isEnquiryDraftClosed()) return;
            setEnquirySession((prev) => (prev ? { ...prev, ...patch } : prev));
        },
        [setEnquirySession],
    );

    const saveEnquirySession = useCallback(
        (values: Partial<z.infer<typeof formSchema>>, files?: File[]) => {
            // Logout has taken the draft away and the page is on its way out.
            // A form mounted through that window still holds every field, so
            // without this any save between the clear and the reload puts the
            // last member's contact details back for the next reader.
            if (isEnquiryDraftClosed()) return;

            const serializableFiles = files?.map((file) => ({
                name: file.name,
                type: file.type,
                size: file.size,
                lastModified: file.lastModified,
            }));

            setEnquirySession({
                name: values.name,
                email: values.email,
                phoneNumber: values.phoneNumber,
                postcode: values.postcode,
                branchId:
                    includeBranch &&
                    values.branchId &&
                    professional.branchOptions.some(
                        (branch) =>
                            branch.value.branchId === Number(values.branchId),
                    )
                        ? Number(values.branchId)
                        : undefined,
                buildHubId: values.buildHubId
                    ? Number(values.buildHubId)
                    : undefined,
                message: values.message,
                siteTreeId: siteTreeID,
                // Persist the bulk ids too so post-login auto-submit re-sends the
                // whole selection rather than just the single keyed item.
                siteTreeIds: siteTreeIDs,
                source,
                professional: {
                    ID: professional.ID,
                    Title: professional.Title,
                },
                itemType,
                relatedItem,
                files: serializableFiles,
                isModal,
            });
        },
        [
            includeBranch,
            professional,
            source,
            itemType,
            siteTreeID,
            siteTreeIDs,
            relatedItem,
            isModal,
            setEnquirySession,
        ],
    );

    const resetEnquirySession = useCallback(
        (messageOnly: boolean = false) => {
            if (messageOnly) {
                setEnquirySession((session) => ({ ...session, message: '' }));
            } else {
                saveEnquirySession({});
            }
            setEnquiryAutoSubmit(false);
        },
        [setEnquirySession, saveEnquirySession, setEnquiryAutoSubmit],
    );

    const submitEnquiry = useCallback(
        (
            values: z.infer<typeof formSchema>,
            files?: File[],
            buildHubFieldSelection?: BuildHubFieldSelection,
        ) => {
            saveEnquirySession(values, files);
            setEnquiryFormId(id);
            setIsAutoSubmitted(false);
            const payload = {
                ...values,
                email: isLoggedIn && user.Email ? user.Email : values.email,
                suburbPostcode: values.postcode?.value?.region ?? '',
                // Empty string, not `JSON.stringify(undefined)` — that returns
                // the undefined value, which reaches the action as the literal
                // "undefined" and is stored as the enquiry's location.
                locationResponse: values.postcode?.value
                    ? JSON.stringify(values.postcode.value)
                    : '',
                siteTreeId: siteTreeID,
                // Callers may opt out of sharing contact details explicitly
                // (e.g. a phone-less form). Otherwise share only when a phone
                // is known — the backend rejects sharing without one.
                shareDetails:
                    (values as { shareDetails?: string }).shareDetails ??
                    (values.phoneNumber ? 'true' : 'false'),
                targetProfessionalId: professional.ID,
                // Bulk enquiry ids, submitted in place of the single id above when
                // the caller selected multiple items. The route drops the single
                // target for these so the backend splits them per item owner.
                siteTreeIds: siteTreeIDs
                    ? JSON.stringify(siteTreeIDs)
                    : undefined,
                buildHubId: values.buildHubId
                    ? Number(values.buildHubId)
                    : undefined,
                buildHubFieldSelection:
                    values.buildHubId && buildHubFieldSelection
                        ? JSON.stringify(buildHubFieldSelection)
                        : undefined,
            };
            const formData = new FormData();
            for (const [key, value] of Object.entries(payload)) {
                if (value !== undefined && value !== null) {
                    formData.append(key, String(value));
                }
            }
            // Add files if present
            if (files?.length) {
                files.forEach((file, index) => {
                    formData.append(`Attachments[${index}]`, file);
                });
            }

            fetcher.submit(formData, {
                method: 'POST',
                action: '/remix-api/enquiry/submit',
                encType: 'multipart/form-data',
            });

            setEnquiryAutoSubmit(false);
            // Carried only a label until now, so nothing about this event
            sentFanOutRef.current = siteTreeIDs ? fanOut : undefined;
            // Left as it was: EnquirySubmit is on the ArchiPro pipeline's
            // ignore list, so nothing added here would ever arrive. The
            // submit-attempt step the warehouse sees is the next line.
            tracker.log('EnquirySubmit', {
                url: new URL(window.location.href),
                label: professional.Title,
            });
            trackSubmitCreateEnquiryRequest(tracker, {
                source,
                fanOut: sentFanOutRef.current,
            });
            if (itemType === 'professional' || itemType === 'product') {
                trackBrochureRequest(tracker, {
                    type: itemType,
                    region: payload.suburbPostcode,
                });
            } else if (itemType === 'project' && relatedItem) {
                trackProjectEnquire(tracker, {
                    category: relatedItem.category,
                    step: 2,
                    project: relatedItem.title,
                    professional: professional.Title,
                    region: payload.suburbPostcode,
                });
            }
            if (source === 'InPage') {
                tracker.log('OnPageEnquirySend', {
                    url: new URL(window.location.href),
                    targetTracker: 'archiproTracker',
                });
            }
        },
        [
            id,
            fetcher,
            tracker,
            itemType,
            isLoggedIn,
            user,
            professional,
            siteTreeID,
            siteTreeIDs,
            source,
            relatedItem,
            fanOut,
            saveEnquirySession,
            setEnquiryFormId,
            setIsAutoSubmitted,
            setEnquiryAutoSubmit,
        ],
    );

    /**
     * Persist the selected postcode/suburb to the user's profile if they are
     * logged in and don't already have one saved.
     */
    const saveSuburbToProfile = useCallback(
        (postcode: { header: string; value: unknown } | undefined) => {
            if (!isLoggedIn || user.Suburb || !postcode) {
                return;
            }
            const formData = new FormData();
            formData.append('suburb', JSON.stringify(postcode));
            suburbFetcher.submit(formData, {
                method: 'POST',
                action: '/remix-api/member/update-suburb',
            });
        },
        [isLoggedIn, user, suburbFetcher],
    );

    const myOnSuccess = useCallback(() => {
        // In-page auto-submits also flow through useEnquiryAutoSubmit's
        // success handler, which logs CreatedEnquiry — don't count it twice.
        if (!isAutoSubmitted) {
            trackCreatedEnquiry(tracker, {
                source: enquirySession?.source ?? source,
                fanOut: sentFanOutRef.current,
                professionalId: professional.ID,
            });
        }
        if (
            fetcher.data &&
            'data' in fetcher.data &&
            fetcher.data?.data &&
            'GeneratedMember' in fetcher.data.data &&
            fetcher.data.data.GeneratedMember
        ) {
            trackAuthEvent({
                tracker,
                event: 'new_account_registered',
                provider: 'AutoGenerated',
                authSource: 'enquiry',
                guestID: fetcher.data.data.GeneratedMember.TrackedGuest?.ID,
            });
        }
        setEnquirySession((session) => ({ ...session, message: '' }));
        saveSuburbToProfile(enquirySession?.postcode ?? undefined);
        triggerSuccess();
    }, [
        fetcher,
        tracker,
        enquirySession,
        source,
        isAutoSubmitted,
        professional,
        saveSuburbToProfile,
        setEnquirySession,
        triggerSuccess,
    ]);

    const isHandleSubmitComplete = useRef(true);
    useEffect(() => {
        if (
            enquiryFormId !== id &&
            !(!enquirySession?.isModal && isAutoSubmitted)
        ) {
            // Only run for the form that was submitted.
            // If the form is in-page and auto-submitted, run this anyway to
            // show the success animation.
            return;
        } else if (fetcher.state === 'submitting') {
            // Ensure this is only run once per submission.
            isHandleSubmitComplete.current = false;
            return;
        } else if (isHandleSubmitComplete.current || !fetcher.data) {
            return;
        }
        // Deliberately not waiting for `idle`. A fetcher goes
        // submitting → loading → idle, where `loading` is the route
        // revalidation that follows the action, and on a detail page that is
        // its whole loader set — around a second, measured. The enquiry has
        // already been accepted by then and nothing shown next reads from those
        // loaders, so the reader spent that second watching a spinner for work
        // that was not theirs.
        const isSuccess = assertSuccessResponse(fetcher.data);
        const isError = assertErrorResponse(fetcher.data);
        if (isSuccess) {
            myOnSuccess();
        } else if (isError) {
            const publicUser =
                'publicUser' in fetcher.data
                    ? fetcher.data.publicUser
                    : undefined;
            const submitErrorReason = publicUser
                ? publicUser.type === 'existing'
                    ? 'login_required'
                    : 'signup_required'
                : 'server_error';
            // Source and ExtraData are the only custom keys the warehouse
            // pipeline persists; the flat reason serves network QA.
            tracker.log('EnquirySubmitError', {
                url: new URL(window.location.href),
                data: {
                    reason: submitErrorReason,
                    Source: source,
                    ExtraData: JSON.stringify({ reason: submitErrorReason }),
                },
            });
            if (publicUser) {
                // If the user exists but is not logged in, auto submit the enquiry
                // form when the user logs in.
                setEnquiryAutoSubmit(true);
                setAutoSubmitDelay(true);
            }
            setPublicUser(publicUser);
            if (publicUser) {
                // Funnel stage: guest hit the auth wall after submitting the
                // form. Only auth-required responses carry publicUser; generic
                // submit failures must not count as a login prompt.
                trackEnquiryAuthPrompt(tracker, {
                    professionalName: professional.Title,
                    itemID: siteTreeID,
                    itemType,
                    source,
                    professionalID: professional.ID,
                    flow: publicUser.type === 'new' ? 'signup' : 'login',
                });
            }
            // Only auth-required responses offer the auth wall — a generic
            // server_error opens no modal (logging in would not fix it, and
            // the user may already be logged in), so the AuthContext
            // LoginPromptShown impression stays truthful.
            if (publicUser) {
                if (onAuthRequired) {
                    // The revamped gate hosts the auth step in-place; the
                    // auto-submit priming above still runs so the post-auth
                    // resubmit works the same as the legacy path.
                    onAuthRequired(publicUser);
                } else {
                    showAuthModal({
                        authSource: 'enquiry',
                        skipPostSignUp: true,
                        flow:
                            publicUser.type === 'new'
                                ? 'post-enquiry-sign-up'
                                : 'post-enquiry-login',
                        defaultPublicUser: publicUser,
                        message:
                            publicUser.type === 'new'
                                ? 'Create an account to submit your enquiry'
                                : 'Log in to submit your enquiry',
                        onSuccess: () => {
                            hideAuthModal();
                            onAuthSuccess?.();
                        },
                        onAuthSuccess: () => {
                            hideAuthModal();
                            onAuthSuccess?.();
                        },
                        onCancel: () => {
                            hideAuthModal();
                            // Cancel any pending post-login auto-submit so the
                            // form reopens in an editable state.
                            setEnquiryAutoSubmit(false);
                            setAutoSubmitDelay(false);
                            onAuthCancel?.();
                        },
                    });
                }
            }
            onFailure?.();
        }
        isHandleSubmitComplete.current = true;
    }, [
        id,
        source,
        professional,
        itemType,
        siteTreeID,
        enquirySession,
        fetcher,
        tracker,
        enquiryFormId,
        isAutoSubmitted,
        resetEnquirySession,
        setEnquiryAutoSubmit,
        setAutoSubmitDelay,
        setPublicUser,
        showAuthModal,
        hideAuthModal,
        onFailure,
        onAuthSuccess,
        onAuthCancel,
        onAuthRequired,
        myOnSuccess,
    ]);

    const canQuickSubmit = useMemo(() => {
        if (!QUICK_ENQUIRY_ENABLED || !isLoggedIn) return false;
        const v = defaultFormValues;
        return !!(
            v.name &&
            v.email &&
            v.phoneNumber &&
            v.postcode?.header &&
            v.postcode?.value &&
            v.message
        );
    }, [isLoggedIn, defaultFormValues]);

    return {
        /**
         * The Zod form schema.
         */
        formSchema,
        /**
         * The default form values.
         */
        defaultFormValues,
        /**
         * The pre-filled message used when no session message exists.
         */
        defaultMessage,
        /**
         * The professional's custom placeholder shown in the (empty) message
         * box when they have overridden the auto-populated enquiry copy.
         * `undefined` when they have not, so the form uses its default hint.
         */
        messagePlaceholder,
        /**
         * Whether the user has all required data for a one-tap quick submission.
         */
        canQuickSubmit,
        /**
         * The form fetcher state.
         */
        state: fetcher.state,
        /**
         * The latest submit action response from the enquiry fetcher.
         */
        fetcherData: fetcher.data,
        /**
         * Whether the enquiry is being auto-submitted after login.
         * True from page load (when sessionStorage flag is set) through
         * the API call, until the fetcher returns to idle.
         */
        isAutoSubmitting:
            enquiryAutoSubmit || (isAutoSubmitted && fetcher.state !== 'idle'),
        /**
         * The form has been submitted and is waiting for the page to refresh
         * before auto submitting the enquiry.
         */
        autoSubmitDelay,
        /**
         * Set the auto submit delay.
         */
        setAutoSubmitDelay,
        /**
         * Submit the enquiry.
         */
        submitEnquiry,
        /**
         * Save the enquiry session to session storage.
         */
        saveEnquirySession,
        patchEnquirySession,
        /**
         * Reset the enquiry session.
         */
        resetEnquirySession,
        /**
         * Save the selected postcode to the user's profile (only fires for
         * logged-in users who don't already have a suburb saved).
         */
        saveSuburbToProfile,
    };
}

/**
 * Hook to get the current session and state of the enquiry form.
 */
export function useEnquirySession() {
    const fetcher = useFetcher<SubmitActionResponse>({
        key: ENQUIRY_FETCHER_KEY,
    });
    const [enquirySession, setEnquirySession] = useAtom(enquirySessionAtom);
    const [enquiryFormId, setEnquiryFormId] = useAtom(enquiryFormIdAtom);
    const [enquiryAutoSubmit, setEnquiryAutoSubmit] = useAtom(
        enquiryAutoSubmitAtom,
    );
    const [isAutoSubmitted, setIsAutoSubmitted] = useAtom(isAutoSubmittedAtom);
    const [autoSubmitDelay, setAutoSubmitDelay] = useAtom(autoSubmitDelayAtom);

    return useMemo(() => {
        const isDone = fetcher.state === 'idle' && !!fetcher.data;
        return {
            enquirySession,
            enquiryFormId,
            fetcher,
            state: fetcher.state,
            isDone,
            isSuccess: isDone && assertSuccessResponse(fetcher.data!),
            isError: isDone && assertErrorResponse(fetcher.data!),
            enquiryAutoSubmit,
            isAutoSubmitted,
            autoSubmitDelay,
            setEnquirySession,
            setEnquiryFormId,
            setEnquiryAutoSubmit,
            setIsAutoSubmitted,
            setAutoSubmitDelay,
        };
    }, [
        fetcher,
        enquirySession,
        enquiryFormId,
        enquiryAutoSubmit,
        isAutoSubmitted,
        autoSubmitDelay,
        setEnquirySession,
        setEnquiryFormId,
        setEnquiryAutoSubmit,
        setIsAutoSubmitted,
        setAutoSubmitDelay,
    ]);
}

/**
 * Hook to handle enquiry form success events. Multiple components can use this hook to receive onSuccess event
 * even though the component doesn't submit the enquiry.
 *
 * This hook provides a way to trigger success callbacks when an enquiry is
 * successfully submitted. It uses an atom event system to ensure the success
 * callback is called even if the component unmounts during the submission process.
 *
 * @param onSuccess - Optional callback function to execute when enquiry submission succeeds
 * @returns Object containing triggerSuccess function to manually trigger the success event
 */
export function useEnquiryEvent({
    onSuccess,
    onFailure,
}: {
    onSuccess?: () => void;
    /** The post-auth resubmit failed. See {@link enquiryFailureAtom}. */
    onFailure?: () => void;
}) {
    const { trigger: triggerSuccess } = useAtomEvent(
        enquirySuccessAtom,
        onSuccess,
    );
    const { trigger: triggerFailure } = useAtomEvent(
        enquiryFailureAtom,
        onFailure,
    );

    return {
        triggerSuccess,
        triggerFailure,
    };
}
